import { defaultAbiCoder } from '@ethersproject/abi'
import type { RouterBuildResponse, RouterExecutionData } from './types'

// Audit #1: the previous build validator (validateBuild.ts) checked the UR address, execute() selector,
// tx value, and response metadata, but NEVER decoded transaction.data. A response with correct
// metadata could still carry valid-selector calldata that spends the Permit2-approved input
// differently. This module decodes execute(bytes,bytes[],uint256) and binds every security-relevant
// field of the inner Universal Router commands to the swap the user actually accepted.
//
// The command shapes/inputs mirror services/router/src/execution/universalRouter.ts exactly
// (fork-verified). Keep the two in sync. Decoding uses @ethersproject/abi (not viem) because the app
// already depends on it and it avoids viem's jsdom Uint8Array realm mismatch under jest.

const UR_EXECUTE_SELECTOR = '0x3593564c'
const NATIVE = '0x0000000000000000000000000000000000000000'
const ADDRESS_THIS = '0x0000000000000000000000000000000000000002'
// Robinhood Chain wrapped native (WETH). Native legs swap through it.
const WRAPPED_NATIVE = '0x0bd7d308f8e1639fab988df18a8011f41eacad73'

// UR single-byte command ids used by the Robinhood encoder.
const CMD = { WRAP_ETH: 0x0b, V3_SWAP_EXACT_IN: 0x00, PAY_PORTION: 0x06, SWEEP: 0x04, UNWRAP_WETH: 0x0c } as const

// ABI type tuples (ethers string form).
const EXECUTE_ARGS = ['bytes', 'bytes[]', 'uint256']
const V3_SWAP_IN_ARGS = ['address', 'uint256', 'uint256', 'bytes', 'bool', 'uint256[]']
const WRAP_ETH_ARGS = ['address', 'uint256']
const PAY_PORTION_ARGS = ['address', 'address', 'uint256']
const SWEEP_ARGS = ['address', 'address', 'uint256']
const UNWRAP_ARGS = ['address', 'uint256']

export type CalldataValidationResult = { ok: true } | { ok: false; reason: string }

const lower = (a: string) => a.toLowerCase()
const eqAddr = (a: unknown, b: string) => typeof a === 'string' && lower(a) === lower(b)
/** ethers returns uint256 as BigNumber; normalize to bigint for comparison. */
const toBig = (v: { toString(): string }) => BigInt(v.toString())

/** ceilDiv(a,b) for bigints — mirrors services/router/src/routing/fees.ts. */
function ceilDiv(a: bigint, b: bigint): bigint {
  return (a + b - 1n) / b
}
/** Gross floor that leaves >= minNet after an output-side fee — mirrors fees.ts minGrossFromNet. */
function minGrossFromNet(minNet: bigint, feeBps: bigint): bigint {
  return ceilDiv(minNet * 10_000n, 10_000n - feeBps)
}

function commandBytes(commandsHex: string): number[] {
  const hex = commandsHex.startsWith('0x') ? commandsHex.slice(2) : commandsHex
  const out: number[] = []
  for (let i = 0; i < hex.length; i += 2) out.push(parseInt(hex.slice(i, i + 2), 16))
  return out
}

function pathEndpoints(pathHex: string): { first: string; last: string } | null {
  const hex = pathHex.startsWith('0x') ? pathHex.slice(2) : pathHex
  // path = tokenIn(20) ++ [fee(3) ++ token(20)]+  → >= 43 bytes, (len-20) % 23 == 0
  if (hex.length < 43 * 2 || (hex.length / 2 - 20) % 23 !== 0) return null
  return { first: '0x' + hex.slice(0, 40), last: '0x' + hex.slice(-40) }
}

/**
 * Decodes and binds the Universal Router calldata in `build.transaction.data` to the accepted quote
 * (`accepted`) and the intended recipient (`userRecipient`, i.e. the connected wallet). Fail-closed:
 * any decode failure, unexpected command sequence, or field that does not match the accepted intent
 * returns `{ ok:false }` and the caller MUST throw before sendTransaction.
 */
export function validateBuildCalldata(
  build: RouterBuildResponse,
  accepted: RouterExecutionData,
  userRecipient: string,
  opts?: { minDeadline?: bigint; maxDeadline?: bigint },
): CalldataValidationResult {
  const tx = build.transaction
  if (!tx || typeof tx.data !== 'string' || !tx.data.toLowerCase().startsWith(UR_EXECUTE_SELECTOR)) {
    return { ok: false, reason: 'transaction.data is not a UR execute() call' }
  }

  let commandsHex: string
  let inputs: readonly string[]
  let deadline: bigint
  try {
    // Strip the 4-byte selector (8 hex chars) and ABI-decode the remainder.
    const body = '0x' + tx.data.slice(10)
    const [c, ins, dl] = defaultAbiCoder.decode(EXECUTE_ARGS, body)
    commandsHex = c as string
    inputs = ins as string[]
    deadline = toBig(dl)
  } catch {
    return { ok: false, reason: 'failed to decode execute() calldata' }
  }

  if (deadline.toString() !== String(build.deadline)) {
    return { ok: false, reason: 'calldata deadline does not match the build response deadline' }
  }
  // Bound the ABSOLUTE deadline to the window the client requested. The caller passes
  // [now - skew, now + requestedDuration + skew] with a NARROW clock/network allowance. The upper bound
  // stops a router bug/compromise handing back a far-future deadline that lets a stuck tx execute much
  // later. The lower bound is skew-TOLERANT: it rejects deadlines grossly behind the client clock, but
  // deliberately allows up to `skew` seconds of lag (it is NOT a strict "already expired" check — actual
  // expiry is enforced on-chain by the UR against block.timestamp). Internal consistency (calldata ==
  // response) is checked above; these bounds tie it to real wall-clock intent.
  if (opts?.minDeadline !== undefined && deadline < opts.minDeadline) {
    return { ok: false, reason: 'calldata deadline is too far behind the client clock' }
  }
  if (opts?.maxDeadline !== undefined && deadline > opts.maxDeadline) {
    return { ok: false, reason: 'calldata deadline is further out than the requested window' }
  }

  const nativeIn = eqAddr(accepted.tokenIn, NATIVE)
  const nativeOut = eqAddr(accepted.tokenOut, NATIVE)
  const feeBps = BigInt(accepted.fee.feeBps)
  const includeFee = feeBps > 0n
  const effectiveIn = nativeIn ? WRAPPED_NATIVE : lower(accepted.tokenIn)
  const effectiveOut = nativeOut ? WRAPPED_NATIVE : lower(accepted.tokenOut)
  const amountIn = BigInt(accepted.amountIn)
  const minNet = BigInt(build.minimumNetAmountOut)
  const expectedMinGross = minGrossFromNet(minNet, feeBps)

  // Expected command sequence for the route shape.
  const expected: number[] = []
  if (nativeIn) expected.push(CMD.WRAP_ETH)
  expected.push(CMD.V3_SWAP_EXACT_IN)
  if (includeFee) expected.push(CMD.PAY_PORTION)
  expected.push(nativeOut ? CMD.UNWRAP_WETH : CMD.SWEEP)

  const cmds = commandBytes(commandsHex)
  if (cmds.length !== expected.length || cmds.some((b, i) => b !== expected[i])) {
    return { ok: false, reason: `unexpected UR command sequence [${cmds.map((b) => b.toString(16)).join(',')}]` }
  }
  if (inputs.length !== cmds.length) {
    return { ok: false, reason: 'commands/inputs length mismatch' }
  }

  let i = 0
  try {
    if (nativeIn) {
      const [recipient, amount] = defaultAbiCoder.decode(WRAP_ETH_ARGS, inputs[i])
      if (!eqAddr(recipient, ADDRESS_THIS)) return { ok: false, reason: 'WRAP_ETH recipient is not ADDRESS_THIS' }
      if (toBig(amount) !== amountIn) return { ok: false, reason: 'WRAP_ETH amount does not equal amountIn' }
      i++
    }

    const [swRecipient, swAmountIn, swMinOut, swPath, swPayerIsUser, swMinHop] = defaultAbiCoder.decode(
      V3_SWAP_IN_ARGS,
      inputs[i],
    )
    if (!eqAddr(swRecipient, ADDRESS_THIS)) return { ok: false, reason: 'V3_SWAP recipient is not ADDRESS_THIS' }
    if (toBig(swAmountIn) !== amountIn) return { ok: false, reason: 'V3_SWAP amountIn does not match the accepted quote' }
    if (toBig(swMinOut) !== expectedMinGross) return { ok: false, reason: 'V3_SWAP amountOutMin does not match the post-fee floor' }
    if (swPayerIsUser !== !nativeIn) return { ok: false, reason: 'V3_SWAP payerIsUser has the wrong value' }
    if ((swMinHop as readonly unknown[]).length !== 0) return { ok: false, reason: 'V3_SWAP minHopPriceX36 must be empty' }
    // Only the path ENDPOINTS are bound, by intended policy. Today /v1/build rebuilds the signed
    // envelope's exact legs, so the packed path is deterministic; endpoint-only binding is chosen so
    // this validator stays correct if server-side re-routing (choosing different intermediate
    // pools/fee tiers at build time) is ever introduced — pinning the full path would then false-reject
    // legitimate re-routes. Either way the user's economic safety does NOT depend on the intermediate
    // hops: swAmountOutMin is bound to expectedMinGross (derived from the accepted minNet) above and
    // enforced on-chain, so any path must still deliver >= the accepted floor or the swap reverts.
    const ends = pathEndpoints(swPath as string)
    if (!ends) return { ok: false, reason: 'V3_SWAP path is malformed' }
    if (!eqAddr(ends.first, effectiveIn)) return { ok: false, reason: 'V3_SWAP path input token does not match the accepted quote' }
    if (!eqAddr(ends.last, effectiveOut)) return { ok: false, reason: 'V3_SWAP path output token does not match the accepted quote' }
    i++

    if (includeFee) {
      const [feeToken, feeRecipient, bips] = defaultAbiCoder.decode(PAY_PORTION_ARGS, inputs[i])
      if (!eqAddr(feeToken, effectiveOut)) return { ok: false, reason: 'PAY_PORTION token is not the output token' }
      if (!accepted.fee.feeRecipient || !eqAddr(feeRecipient, accepted.fee.feeRecipient)) {
        return { ok: false, reason: 'PAY_PORTION recipient does not match the accepted fee recipient' }
      }
      if (toBig(bips) !== feeBps) return { ok: false, reason: 'PAY_PORTION bips does not match the accepted fee' }
      i++
    }

    if (nativeOut) {
      const [recipient, amountMin] = defaultAbiCoder.decode(UNWRAP_ARGS, inputs[i])
      if (!eqAddr(recipient, userRecipient)) return { ok: false, reason: 'UNWRAP_WETH recipient is not the user' }
      if (toBig(amountMin) !== minNet) return { ok: false, reason: 'UNWRAP_WETH minimum does not equal minNet' }
    } else {
      const [token, recipient, amountMin] = defaultAbiCoder.decode(SWEEP_ARGS, inputs[i])
      if (!eqAddr(token, effectiveOut)) return { ok: false, reason: 'SWEEP token is not the output token' }
      if (!eqAddr(recipient, userRecipient)) return { ok: false, reason: 'SWEEP recipient is not the user' }
      if (toBig(amountMin) !== minNet) return { ok: false, reason: 'SWEEP minimum does not equal minNet' }
    }
  } catch {
    return { ok: false, reason: 'failed to decode a UR command input' }
  }

  // Native input must carry exactly amountIn as msg.value; ERC-20 input must carry none.
  const value = BigInt(tx.value ?? '0')
  if (nativeIn ? value !== amountIn : value !== 0n) {
    return { ok: false, reason: 'transaction.value is inconsistent with the input token' }
  }

  return { ok: true }
}
